import type { ServiceMapper } from '../../../domain/types'
import type { ImpressionViewAncillariesItem } from '@/scopes/checkout/domain/analytics/types'
import type { APIBookingDraft, APIBookingDraftGolfService } from '@/scopes/checkout/infrastructure/api/types'
import type { APIBookingDraftGolfServiceOption } from '@/scopes/checkout/infrastructure/api/types/services/golf'
import {
  CheckoutGolfService,
  GOLF_NEW_SLOT_KEY,
  GolfServiceSelectedValue,
  ServiceType,
} from '@/scopes/checkout/domain/types'
import {
  type GA4ImpressionItemsConfig,
  mapDefaultSelectedItemToGA4,
  type SelectedItemInput,
} from '../model-mappers/ga4AnalyticsMapper'
import { getGA4CategoryByServiceType } from '../model-mappers/ga4AnalyticsMapper'
import {
  createGolfServiceOptionMapper,
  type GolfServiceOptionMapper,
} from '../model-mappers/golfServiceOptionMapper'
import {
  createServiceParticipantsMapper,
  type ServiceParticipantsMapper,
} from '../model-mappers/serviceParticipantsMapper'

interface Dependencies {
  serviceName: string
  serviceTypeMapper: string
  golfServiceOptionMapper?: GolfServiceOptionMapper
  serviceParticipantsMapper?: ServiceParticipantsMapper
  serviceType: ServiceType
}

export function createGolfServiceMapper(dependencies: Dependencies): ServiceMapper<CheckoutGolfService> {
  const {
    serviceName,
    serviceTypeMapper,
    golfServiceOptionMapper = createGolfServiceOptionMapper(),
    serviceParticipantsMapper = createServiceParticipantsMapper(),
    serviceType,
  } = dependencies

  function getServiceName() {
    return serviceName
  }

  function getServiceTypeMapper() {
    return serviceTypeMapper
  }

  function map(bookingDraft: APIBookingDraft): CheckoutGolfService | null {
    const service = bookingDraft.services.find(({ aName }) => aName === serviceName) as APIBookingDraftGolfService

    if (!service)
      return null

    const { aName, options } = service
    const golfOptions = options.slice().sort((a, b) => a.location.localeCompare(b.location)).filter(o => !o.isNoneOption)

    if (golfOptions.length === 0)
      return null

    const optionsMap = golfServiceOptionMapper.mapOptions(golfOptions)
    const locations = golfServiceOptionMapper.mapLocations(golfOptions)
    const participants = buildParticipants(bookingDraft, golfOptions)
    const selectedValues = buildSelectedValues(golfOptions)
    const firstOption = Object.values(optionsMap).reduce<typeof optionsMap[string] | undefined>(
      (earliest, option) => (earliest === undefined || option.date < earliest.date ? option : earliest),
      undefined,
    )
    const noneOptionRaw = options.find(o => o.isNoneOption)
    const isNoneOption = noneOptionRaw ? noneOptionRaw.id : ''

    const editingSelectedValues: Record<string, GolfServiceSelectedValue> = {
      ...selectedValues,
      [GOLF_NEW_SLOT_KEY]: {
        locationId: firstOption?.locationId ?? '',
        date: firstOption?.date ?? '',
        holes: firstOption ? Number(firstOption.holes) : 9,
        withBuggy: firstOption?.withBuggy ?? false,
        time: firstOption?.time ?? '',
        participantIds: [],
        optionId: firstOption?.id ?? '',
      },
    }

    return {
      id: aName,
      type: ServiceType.Golf,
      isSelected: Object.keys(selectedValues).length > 0,
      rooms: bookingDraft.rooms.map(({ id, name, participantIds }) => ({
        id: id.toString(),
        name,
        participantIds: participantIds.map(pid => pid.toString()),
      })),
      participants,
      options: optionsMap,
      locations,
      isNoneOption,
      selectedValues,
      editingSelectedValues,
    }
  }

  function buildParticipants(
    bookingDraft: APIBookingDraft,
    golfOptions: APIBookingDraftGolfServiceOption[],
  ) {
    return bookingDraft.participants.reduce(
      (acc, participant) => {
        const eligibleOptions = golfOptions.filter(o => o.appliesToParticipants.includes(participant.id))
        if (eligibleOptions.length === 0)
          return acc

        const base = serviceParticipantsMapper.mapParticipantWithAvailableOptions(participant, eligibleOptions as any)

        return {
          ...acc,
          [participant.id.toString()]: {
            ...base,
            name: participant.name || participant.alternativeName?.toLowerCase() || '',
            selectedValue: null,
            availableOptions: eligibleOptions.map(o => o.id),
          },
        }
      },
      {},
    )
  }

  function buildSelectedValues(
    options: APIBookingDraftGolfServiceOption[],
  ): Record<string, GolfServiceSelectedValue> {
    return options
      .filter(o => o.isSelected && o.assignedParticipants.length > 0)
      .reduce(
        (acc, option) => {
          const id = option.id
          return {
            ...acc,
            [id]: {
              locationId: golfServiceOptionMapper.buildLocationId(option.location),
              date: option.date,
              holes: Number(option.holes),
              time: option.time,
              withBuggy: Boolean(option.hasBuggy),
              participantIds: option.assignedParticipants.map(pid => pid.toString()),
              optionId: id,
            },
          }
        },
        {} as Record<string, GolfServiceSelectedValue>,
      )
  }

  function mapSelection(service: CheckoutGolfService) {
    return Object.values(service.selectedValues).map(slot => ({
      value: service.options[slot.optionId]?.code ?? slot.optionId,
      date: service.options[slot.optionId]?.date ?? null,
      participants: slot.participantIds,
      time: slot?.time ?? service.options[slot.optionId]?.time ?? null,
    }))
  }

  function mapGA4ImpressionItems(bookingDraft: APIBookingDraft, _config?: GA4ImpressionItemsConfig): ImpressionViewAncillariesItem[] {
    const service = bookingDraft.services.find(({ aName }) => aName === serviceName) as APIBookingDraftGolfService | undefined
    if (!service)
      return []

    return service.options
      .filter(option => !option.isNoneOption)
      .map(option => ({
        item_name: option.displayText,
        item_id: option.code,
        price: option.price,
        item_category: `Ancillary > Additional cost > ${getGA4CategoryByServiceType(serviceType, service.aName)}`,
        item_category2: getGA4CategoryByServiceType(serviceType, service.aName),
        item_list_name: 'checkout1',
        quantity: '1',
      }))
  }

  function mapSelectedItemToGA4(service: CheckoutGolfService, step: number): ImpressionViewAncillariesItem[] {
    const selectedItems = Object.entries(service.selectedValues).reduce((items: SelectedItemInput[], [slotId, slot]) => {
      const option = service.options[slot.optionId]
      if (!option)
        return items

      items.push({
        label: option.label || '',
        value: slotId,
        price: option.price,
      })

      return items
    }, [])

    return mapDefaultSelectedItemToGA4(selectedItems, {
      serviceType,
      serviceId: service.id,
      step,
    })
  }

  return {
    getServiceName,
    getServiceTypeMapper,
    map,
    mapSelection,
    mapGA4ImpressionItems,
    mapSelectedItemToGA4,
  }
}
